<template>
  <v-flex>
    <v-row class="mt-n7" v-if="!loading" justify="center" no-gutters>
      <v-col cols="12" md="10">
        <v-container v-if="data_invoice != null" fluid>
          <!-- Modern Header -->
          <v-row no-gutters class="mb-4 align-center animate__animated animate__fadeIn">
            <!-- Izquierda: tipo + número + título -->
            <v-col cols="12" md="7" class="d-flex align-center flex-wrap" style="gap:16px;">
              <v-chip v-if="data_invoice.note_type === 'CREDIT'" color="blue darken-1" dark
                class="font-weight-black elevation-2" label>
                <v-icon left small>mdi-arrow-down-circle-outline</v-icon> NOTA DE CRÉDITO
              </v-chip>
              <v-chip v-else-if="data_invoice.note_type === 'DEBIT'" color="deep-orange darken-1" dark
                class="font-weight-black elevation-2" label>
                <v-icon left small>mdi-arrow-up-circle-outline</v-icon> NOTA DE DÉBITO
              </v-chip>
              <div class="d-flex align-center flex-wrap" style="gap:6px;">
                <span class="text-h5 font-weight-black primary--text glow-text">#{{ data_invoice?.invoice_note_number }}</span>
                <span class="grey--text text--lighten-2 font-weight-thin mx-1">·</span>
                <span class="text-body-1 grey--text text--darken-1">{{ formTitle }}</span>
              </div>
            </v-col>

            <!-- Derecha: estado + fecha -->
            <v-col cols="12" md="5" class="d-flex align-center justify-end" style="gap:16px;">
              <div class="d-flex flex-column text-right">
                <span class="text-overline grey--text font-weight-black line-height-1 mb-1">Fecha de emisión</span>
                <div class="d-flex align-center justify-end">
                  <v-icon x-small class="mr-1" color="primary">mdi-clock-outline</v-icon>
                  <span class="text-caption font-weight-bold grey--text text--darken-2">{{
                    getDate(data_invoice.effective_date, 1) }}</span>
                </div>
              </div>
              <v-chip v-if="data_invoice.status != true" color="error" dark class="font-weight-black elevation-1" label>
                <v-icon left small>mdi-cancel</v-icon> ANULADA
              </v-chip>
              <v-chip v-else color="#2d8575" dark class="font-weight-black elevation-2" label>
                <v-icon left small>mdi-check-circle</v-icon> COMPROBANTE ACTIVO
              </v-chip>
            </v-col>
          </v-row>

          <!-- Action Bar -->
          <v-row no-gutters class="mb-3 mx-2 align-center" justify="start">
            <v-btn @click="beforeRoute" v-if="hasPreviousPage" depressed class="mr-2 rounded-lg text-capitalize px-4"
              color="primary" text>
              <v-icon left size="20">mdi-arrow-left</v-icon>
              Volver
            </v-btn>

            <div class="d-flex align-center bg-white rounded-lg px-1 py-1 border ml-2">
              <v-btn v-if="data_invoice.status == true" @click="downloadInvoice" text
                class="rounded-lg text-capitalize px-4" color="primary">
                <v-icon left size="18">mdi-printer-outline</v-icon>
                Imprimir
              </v-btn>
            </div>

            <v-btn v-if="data_invoice.status == true && data_invoice.recorded == false"
              @click="dialogCancelInvoice = true" depressed class="ml-4 rounded-lg text-capitalize px-4"
              color="error lighten-5">
              <v-icon left size="20">mdi-trash-can-outline</v-icon>
              Anular Nota
            </v-btn>

            <v-spacer></v-spacer>

            <div class="d-flex align-center">
              <v-btn v-if="$store.state.auth.user.user.user_id == 154" @click="sendToDGII" depressed
                class="rounded-lg mr-4 text-capitalize px-6" color="primary">
                <v-icon left>mdi-send-check</v-icon>Enviar a DGII
              </v-btn>

              <div class="d-flex align-center gap-2 mr-4">
                <v-chip v-if="data_invoice.use_ecf && is_valid_dgii" color="primary lighten-5" text-color="primary"
                  class="font-weight-black px-4" label>
                  Enviada a DGII
                  <v-icon right small>mdi-certificate</v-icon>
                </v-chip>
                <v-chip v-else-if="data_invoice.use_ecf && !is_valid_dgii && data_invoice.note_ncf != null && data_invoice.note_ncf.startsWith('E')"
                  color="warning lighten-5" text-color="warning darken-2" class="font-weight-black px-4" label>
                  Error DGII
                  <v-icon right small>mdi-alert</v-icon>
                </v-chip>
              </div>

              <div class="d-flex align-center gap-2">
                <v-chip v-if="data_invoice.recorded" color="success lighten-5" text-color="success darken-2"
                  class="font-weight-black px-4" label>
                  <v-icon left small>mdi-check-decagram</v-icon> CONTABILIZADA
                </v-chip>
                <v-chip v-else-if="data_invoice.status == true" color="warning lighten-5" text-color="warning darken-2"
                  class="font-weight-black px-4 pulse-active" label>
                  <v-icon left small>mdi-alert-circle-outline</v-icon> PENDIENTE ASIENTO
                </v-chip>

                <v-btn v-if="data_invoice.recorded == false && data_invoice.status == true"
                  @click="dialogAccountingInvoice = true" depressed class="ml-2 rounded-lg text-capitalize px-6"
                  color="success">
                  <v-icon left>mdi-book-plus-outline</v-icon> Crear Asiento
                </v-btn>

                <v-btn v-else-if="data_invoice.recorded == true && data_invoice.status == true"
                  @click="dialogCancelAccountingInvoice = true" depressed class="ml-2 rounded-lg text-capitalize px-4"
                  color="error lighten-2">
                  <v-icon left size="20">mdi-book-remove-outline</v-icon> Anular Asiento
                </v-btn>
              </div>
            </div>
          </v-row>

          <!-- Alert for ECF notes not sent to DGII -->
          <v-row
            v-if="!loading && data_invoice && data_invoice.use_ecf && !is_valid_dgii && data_invoice.note_ncf != null && data_invoice.note_ncf.startsWith('E')"
            no-gutters justify="center" class="mt-4">
            <v-col cols="12">
              <v-alert type="warning" prominent border="left" colored-border elevation="2">
                <v-row align="center">
                  <v-col class="grow">
                    <div class="text-h6 font-weight-bold">
                      <v-icon large left color="warning">mdi-alert-circle</v-icon>
                      Esta nota electrónica (e-NCF) no ha sido enviada a la DGII
                    </div>
                    <div class="mt-2">
                      Es necesario enviar esta nota a la DGII para cumplir con las regulaciones fiscales.
                    </div>
                  </v-col>
                  <v-col class="shrink">
                    <v-btn v-if="$store.state.auth.user.user.user_id == 154" @click="sendToDGII" color="warning" large
                      elevation="2">
                      <v-icon left>mdi-certificate</v-icon>
                      Enviar a DGII
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Summary Cards -->
          <v-row no-gutters class="mt-4 gap-4">
            <v-col v-for="(card, index) in summaryCards" :key="index" class="pa-2">
              <v-card class="summary-card" :class="card.colorClass" outlined>
                <v-card-text>
                  <div class="d-flex align-center mb-1">
                    <v-icon small class="mr-2" :color="card.iconColor">{{ card.icon }}</v-icon>
                    <span class="text-overline font-weight-bold">{{ card.title }}</span>
                  </div>
                  <div class="d-flex align-baseline">
                    <span class="text-h5 font-weight-black mr-1">{{ currencyFormat(card.value) }}</span>
                    <span class="text-caption font-weight-medium">{{ data_invoice.currency_code }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Document Body -->
          <v-row class="mt-4" no-gutters justify="center">
            <div class="folded_corner mt-5 overflow-hidden position-relative">
              <!-- Company Header -->
              <v-row class="mt-8 px-10 align-center">
                <v-col cols="12" md="3">
                  <v-img contain width="140" height="auto" :src="locationImage" class="mx-auto mx-md-0"></v-img>
                </v-col>

                <v-col cols="12" md="6" class="text-center">
                  <h2 class="text-h4 font-weight-black grey--text text--darken-4 mb-1 letter-spacing-1">
                    {{ data_invoice.business_unit_description }}
                  </h2>
                  <div class="text-subtitle-1 font-weight-bold grey--text text--darken-1">
                    {{ data_invoice.location_description }}
                  </div>
                  <div class="text-caption grey--text mt-1">
                    RNC: {{ data_invoice.business_unit_rnc }}
                  </div>
                </v-col>

                <v-col cols="12" md="3" class="text-right d-flex flex-column justify-center">
                  <span class="text-h5 font-weight-black grey--text text--lighten-2 line-height-1 mb-2">#{{
                    data_invoice.invoice_note_number }}</span>
                  <div>
                    <span class="text-caption grey--text font-weight-bold">Creación:</span>
                    <span class="text-body-2 font-weight-black grey--text text--darken-4 ml-1">{{
                      getDate(data_invoice.created_date, 2) }}</span>
                  </div>
                </v-col>
              </v-row>

              <!-- Client/Supplier Info Section -->
              <div class="px-10 mt-6">
                <v-row class="bg-white py-4 rounded-lg border mx-0 align-center shadow-sm">
                  <v-col cols="12" md="7" class="border-right-light">
                    <!-- Fila 1: Cliente | RNC/Cédula -->
                    <v-row dense>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <span class="text-caption grey--text font-weight-bold width-label">{{
                            [1, 4].includes(data_invoice.invoice_type_id) ? 'Proveedor' : 'Cliente' }}:</span>
                          <span
                            class="text-body-1 font-weight-black primary--text text-decoration-underline cursor-pointer ml-3"
                            @click="viewEntity(data_invoice.entity_id)">
                            {{ data_invoice.entity_name }}
                          </span>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <span class="text-caption grey--text font-weight-bold width-label">RNC/Cédula:</span>
                          <span class="text-body-2 font-weight-bold grey--text text--darken-3 ml-3">{{
                            data_invoice.entity_document_id }}</span>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Fila 2: Nota Suplidor | Factura Afectada -->
                    <v-row dense>
                      <v-col cols="6">
                        <div class="d-flex align-center" v-if="data_invoice.supplier_invoice_number">
                          <span class="text-caption grey--text font-weight-bold width-label">Nota Suplidor:</span>
                          <span class="text-body-2 font-weight-black grey--text text--darken-4 ml-3">{{
                            data_invoice.note_provider_number }}</span>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center" v-if="data_invoice.invoice_number">
                          <span class="text-caption grey--text font-weight-bold width-label">Factura Afectada:</span>
                          <span
                            class="text-body-2 font-weight-bold primary--text text-decoration-underline cursor-pointer ml-3"
                            @click="showInvoice(data_invoice.invoice_id, true)">
                            {{ data_invoice.invoice_number }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Fila 3: NCF Suplidor | Factura Suplidor Afectada -->
                    <v-row dense>
                      <v-col cols="6">
                        <div class="d-flex align-center" v-if="data_invoice.note_provider_nfc">
                          <span class="text-caption grey--text font-weight-bold width-label">NCF Suplidor:</span>
                          <span class="text-body-2 font-weight-black grey--text text--darken-4 ml-3">{{
                            data_invoice.note_provider_nfc }}</span>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center" v-if="data_invoice.supplier_invoice_number">
                          <span class="text-caption grey--text font-weight-bold width-label">Factura Suplidor Afectada:</span>
                          <span class="text-body-2 font-weight-black grey--text text--darken-4 ml-3">{{
                            data_invoice.supplier_invoice_number }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="12" md="5">
                    <div class="px-4">
                      <div class="d-flex justify-space-between mb-2" v-if="data_invoice.invoice_ncf_code">
                        <span class="text-caption grey--text font-weight-bold">NCF Factura Afectada:</span>
                        <span class="text-body-2 font-weight-black grey--text text--darken-4">{{
                          data_invoice.invoice_ncf_code }}</span>
                      </div>
                      <div class="d-flex justify-space-between" v-if="data_invoice.account_date">
                        <span class="text-caption grey--text font-weight-bold">Fecha Contable:</span>
                        <span class="text-body-2 font-weight-black grey--text text--darken-4">{{
                          getDate(data_invoice.account_date, 2) }}</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mx-10 mb-6"></v-divider>

              <!-- NCF Hero Bar -->
              <v-row class="px-10 mb-6 mt-n2">
                <v-col cols="12">
                  <div
                    class="ncf-hero-bar py-3 px-6 rounded-lg d-flex align-center justify-space-between border position-relative overflow-hidden">
                    <div class="d-flex align-center">
                      <div class="mr-4">
                        <span class="text-overline grey--text font-weight-black d-block mb-n1">{{
                          getDocumentTypeName(data_invoice.note_ncf) }}</span>
                        <span class="text-h5 font-weight-black primary--text letter-spacing-1">{{
                          data_invoice.note_ncf || 'Sin NCF' }}</span>
                      </div>
                      <v-chip v-if="data_invoice.invoice_ncf_code" color="error lighten-5" text-color="error" label
                        small class="font-weight-black">
                        MODIFICA A: {{ data_invoice.invoice_ncf_code }}
                      </v-chip>
                    </div>

                    <div class="d-flex align-center">
                      <div class="text-right px-4 border-right-light">
                        <span class="text-overline grey--text font-weight-black d-block line-height-1">Emisión</span>
                        <span class="text-subtitle-2 font-weight-black grey--text text--darken-3">{{
                          getDate(data_invoice.effective_date, 2) }}</span>
                      </div>
                      <div class="text-right pl-4" v-if="data_invoice.account_date">
                        <span
                          class="text-overline grey--text font-weight-black d-block line-height-1">Contabilización</span>
                        <span class="text-subtitle-2 font-weight-black grey--text text--darken-3">{{
                          getDate(data_invoice.account_date, 2) }}</span>
                      </div>
                    </div>
                    <div class="ncf-accent-bg"></div>
                  </div>
                </v-col>
              </v-row>

              <!-- Data Table -->
              <v-row class="mt-7 px-10">
                <v-col cols="12">
                  <v-data-table :headers="headers" dense :items="data_invoice.invoice_details" :items-per-page="4000"
                    class="elevation-0 rounded-lg custom-table" hide-default-footer>
                    <!-- <template v-slot:header="{ props: { headers } }">
                        <thead>
                          <tr>
                            <th
                              style="background-color: #f9fafd"
                              v-for="(h, i) in headers"
                              :class="h.class"
                              :key="i"
                            >
                              <span>{{ h.text }}</span>
                            </th>
                          </tr>
                        </thead>
                      </template> -->
                    <template v-slot:[`item.price`]="{ item }">
                      <span>
                        {{ currencyFormat(item.adjustment_amount) }}
                      </span>
                    </template>
                    <template v-slot:[`item.discount`]="{ item }">
                      <span>
                        {{
                          currencyFormat(
                            parseFloat(item.adjustment_amount) *
                            parseFloat(item.quantity)
                          )
                        }}
                      </span>
                    </template>
                    <template v-slot:[`item.itbis`]="{ item }">
                      <span>
                        {{ currencyFormat(0) }}
                      </span>
                    </template>
                    <template v-slot:[`item.total`]="{ item }">
                      <span>
                        {{ currencyFormat(item.adjustment_amount * item.quantity) }}
                      </span>
                    </template>
                    <template v-slot:footer>
                      <v-row class="mt-8 px-4" justify="space-between" align="start">
                        <!-- Signature Box -->
                        <v-col cols="12" md="4">
                          <div class="signature-box elaborated">
                            <div class="d-flex align-center mb-3">
                              <v-avatar size="40" color="primary lighten-4" class="mr-3">
                                <span class="text-subtitle-2 font-weight-black primary--text">{{
                                  getInitials(data_invoice.user) }}</span>
                              </v-avatar>
                              <div class="d-flex flex-column">
                                <span class="text-overline grey--text font-weight-black line-height-1 mb-1">Elaborado
                                  por</span>
                                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                                  data_invoice.user }}</span>
                              </div>
                            </div>
                          </div>
                        </v-col>

                        <!-- Totals Card -->
                        <v-col cols="12" md="5">
                          <v-card class="rounded-lg totals-display" color="grey lighten-5" flat>
                            <v-card-text class="pb-2 pt-3">
                              <div class="d-flex justify-space-between align-center mb-2">
                                <span class="text-caption grey--text font-weight-bold">Subtotal</span>
                                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                                  currencyFormat(data_invoice.subtotal_amount) }} {{ data_invoice.currency_code
                                  }}</span>
                              </div>
                              <div class="d-flex justify-space-between align-center mb-2"
                                v-if="parseFloat(data_invoice.discount_amount) > 0">
                                <span class="text-caption grey--text font-weight-bold">Descuento</span>
                                <span class="text-body-2 font-weight-bold error--text">-{{
                                  currencyFormat(data_invoice.discount_amount) }} {{ data_invoice.currency_code
                                  }}</span>
                              </div>
                              <div class="d-flex justify-space-between align-center mb-2"
                                v-if="parseFloat(data_invoice.taxes_amount) > 0">
                                <span class="text-caption grey--text font-weight-bold">ITBIS</span>
                                <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{
                                  currencyFormat(data_invoice.taxes_amount) }} {{ data_invoice.currency_code }}</span>
                              </div>
                            </v-card-text>
                            <v-divider></v-divider>
                            <v-card-text class="py-3">
                              <div class="d-flex justify-space-between align-center">
                                <span class="text-subtitle-2 font-weight-black grey--text text--darken-4">TOTAL</span>
                                <div>
                                  <span class="text-h5 font-weight-black primary--text letter-spacing-1">{{
                                    currencyFormat(data_invoice.total_amount) }}</span>
                                  <span class="text-body-2 font-weight-black grey--text text--darken-2 ml-1">{{
                                    data_invoice.currency_code }}</span>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <!-- Notes Section -->
              <v-row class="px-10 mb-10 mt-10">
                <v-col cols="12">
                  <div class="note-section pa-4 rounded-lg">
                    <div class="d-flex align-center mb-2">
                      <v-icon small color="grey darken-1" class="mr-2">mdi-text-box-outline</v-icon>
                      <span class="text-overline grey--text text--darken-1 font-weight-black">Motivo / Razón</span>
                    </div>
                    <p class="text-body-2 grey--text text--darken-3 mb-0" style="white-space: pre-line;">{{
                      data_invoice.reason || 'Sin motivo especificado' }}</p>
                  </div>
                </v-col>
              </v-row>

            </div>
          </v-row>

          <!-- Accounting Section -->
          <v-row v-if="data_invoice.invoice_type_id != 6" class="mt-8" no-gutters justify="center">
            <v-col cols="12" md="12"><v-card height="auto">
                <v-tabs v-model="tab" background-color="grey lighten-5 ">
                  <v-tab v-for="(item, i) in itemsTab" :key="i">
                    <span class="text-capitalize"> {{ item }}</span>
                  </v-tab>
                </v-tabs>
                <div v-show="tab === 0">
                  <v-row class="mt-3"
                    v-if="post_accounting_computed.length > 0 && parseFloat(data_invoice?.total_amount || 0) > 0">
                    <PostAccounting :cant_edit="can_edit_accounting"
                      :search_names="true" :order_item_entries="post_accounting_computed"
                      :total_amount="parseFloat(data_invoice?.total_amount || 0)"
                      :total_amount_base="`${accounting[0]?.business_unit_currency_code}$${currencyFormat(parseFloat(data_invoice?.total_amount) * parseFloat(accounting[0]?.currency_rate) || 0)}`"
                      :totalAmountBase="parseFloat(data_invoice?.total_amount) * (parseFloat(accounting[0]?.currency_rate) || 1)"
                      :show_credits="true" :business_unit_id="data_invoice.business_unit_id"
                      :diary_book_code="accounting[0]?.diary_book_code || 'N/A'" @set_accounts="save_acounting_invoice" />
                  </v-row>
                  <div v-else class="text-center py-12">
                    <v-icon size="64" color="grey lighten-2">mdi-book-remove</v-icon>
                    <p class="text-h6 grey--text text--lighten-1 mt-4">La nota no ha sido contabilizada aún</p>
                  </div>
                </div>
                <div v-show="tab === 1" class="pa-4">
                  <div v-if="invoice_warehouse_details && invoice_warehouse_details.length > 0">
                    <v-data-table :headers="headersInventario" :items="invoice_warehouse_details" :items-per-page="10"
                      class="elevation-0" :footer-props="{
                        'items-per-page-options': [10, 25, 50, 100, -1],
                        'items-per-page-text': 'Movimientos por página:'
                      }">
                      <template v-slot:[`item.item_name`]="{ item }">
                        <div class="d-flex align-center">
                          <v-icon small class="mr-2" :color="item.entry_type_id === 1 ? 'error' : 'success'">
                            {{ item.entry_type_id === 1 ? 'mdi-minus-box' : 'mdi-plus-box' }}
                          </v-icon>
                          <span class="font-weight-bold">{{ item.item_name }}</span>
                        </div>
                      </template>
                      <template v-slot:[`item.quantity`]="{ item }">
                        <span :class="parseFloat(item.quantity) < 0 ? 'error--text' : 'success--text'"
                          class="font-weight-black">
                          {{ item.quantity }}
                        </span>
                      </template>
                      <template v-slot:[`item.effective_date`]="{ item }">
                        <span class="grey--text">{{ getDate(item.effective_date, 1) }}</span>
                      </template>
                    </v-data-table>
                  </div>
                  <div v-else class="text-center py-12">
                    <v-icon size="64" color="grey lighten-3">mdi-warehouse-off</v-icon>
                    <p class="text-h6 grey--text text--lighten-1 mt-4">Sin movimientos de inventario asociados</p>
                  </div>
                </div>
                <!-- <v-row class="mt-0" justify="center">
                  <v-col v-if="accounting.length > 0" cols="12" md="12">
                    <v-data-table
                      :headers="headersContabilidad"
                      :items="accounting"
                      class="elevation-0 rounded-lg mt-4 mb-4"
                      hide-default-footer
                      :items-per-page="100"
                    >
                      <template v-slot:[`item.chart_of_account_name`]="{ item }">
                        <span class="primary--text font-weight-medium">
                          {{ item.chart_of_account_code }}
                        </span>
                        -
                        <span class="font-weight-light">
                          {{ item.chart_of_account_name }}
                        </span>
                      </template>

                      <template v-slot:[`item.debito`]="{ item }">
                        <span v-if="item.entry_type_id == 1" class="success--text">
                          {{ currencyFormat(item.amount) }} {{ item.currency_code }}
                        </span>
                      </template>
                      <template v-slot:[`item.credito`]="{ item }">
                        <span v-if="item.entry_type_id == 2" class="error--text">
                          {{ currencyFormat(item.amount) }} {{ item.currency_code }}
                        </span>
                      </template>
                      <template v-slot:[`item.debitoBase`]="{ item }">
                        <span v-if="item.entry_type_id == 1" class="success--text">
                          {{ currencyFormat(item.amount * item.currency_rate) }}
                          {{ item.business_unit_currency_code }}
                        </span>
                      </template>
                      <template v-slot:[`item.creditoBase`]="{ item }">
                        <span v-if="item.entry_type_id == 2" class="error--text">
                          {{ currencyFormat(item.amount * item.currency_rate) }}
                          {{ item.business_unit_currency_code }}
                        </span>
                      </template>

                      <template v-slot:body.append>
                        <tr class="accent lighten-4">
                          <td colspan="6" class="text-right font-weight-bold">TOTAL</td>
                          <td class="text-right font-weight-bold success--text">
                            {{ currencyFormat(getTotalDebito()) }}
                            {{ accounting[0].currency_code }}
                          </td>
                          <td class="text-right font-weight-bold error--text">
                            {{ currencyFormat(getTotalCredito()) }}
                            {{ accounting[0].currency_code }}
                          </td>
                          <td class="text-right font-weight-bold success--text">
                            {{ currencyFormat(getTotalDebitoBase()) }}
                            {{ accounting[0].business_unit_currency_code }}
                          </td>
                          <td class="text-right font-weight-bold error--text">
                            {{ currencyFormat(getTotalCreditoBase()) }}
                            {{ accounting[0].business_unit_currency_code }}
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-col>
                  <div class="mt-5 mb-5" v-else>No hay contabilidad</div>
                </v-row> -->
              </v-card>
            </v-col>
          </v-row>

          <!-- Metadata and Attachments Cards -->
          <v-row class="mt-8" no-gutters>
            <v-col cols="12" md="6" class="pr-md-3">
              <v-card class="rounded-lg border" flat>
                <v-card-text>
                  <div class="d-flex align-center mb-4">
                    <v-icon color="primary" class="mr-3">mdi-calendar-clock</v-icon>
                    <span class="text-overline grey--text font-weight-black">Información del Documento</span>
                  </div>
                  <div class="header-meta-row d-flex align-center justify-space-between">
                    <div class="d-flex flex-column">
                      <span class="text-caption grey--text font-weight-bold">Fecha de Creación</span>
                      <span class="text-body-1 font-weight-black grey--text text--darken-4">{{
                        getDate(data_invoice.created_date, 1) }}</span>
                    </div>
                    <div class="d-flex flex-column text-right" v-if="data_invoice.user">
                      <span class="text-caption grey--text font-weight-bold">Elaborado por</span>
                      <div class="d-flex align-center justify-end">
                        <v-avatar size="24" color="primary lighten-4" class="mr-2">
                          <span class="text-caption font-weight-black primary--text">{{ getInitials(data_invoice.user)
                          }}</span>
                        </v-avatar>
                        <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{ data_invoice.user
                        }}</span>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" class="pl-md-3">
              <v-card class="rounded-lg border" flat min-height="140">
                <v-card-text>
                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="d-flex align-center">
                      <v-icon color="primary" class="mr-3">mdi-paperclip</v-icon>
                      <span class="text-overline grey--text font-weight-black">Archivos Adjuntos</span>
                    </div>
                    <v-btn v-if="data_invoice.storage_json != null" @click="takeImage" x-small depressed color="primary"
                      class="rounded-lg">
                      <v-icon small left>mdi-plus</v-icon> Agregar
                    </v-btn>
                  </div>
                  <div v-if="data_invoice.storage_json == null" @click="takeImage"
                    class="border-dashed rounded-lg pa-4 text-center cursor-pointer">
                    <v-icon size="32" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
                    <p class="text-caption grey--text mb-0 mt-2">Click para agregar archivos</p>
                  </div>
                  <v-list v-else dense class="py-0">
                    <v-list-item v-for="(file, i) in itemsFiles" :key="i" class="px-0" @click="viewAddtached(file)">
                      <v-list-item-icon class="mr-3">
                        <v-icon small color="primary">mdi-file-document-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption font-weight-bold">{{ file.description
                        }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-icon small color="grey">mdi-eye-outline</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
            <v-file-input v-show="false" style="opacity: 0" name="upload" id="fileInput"
              @change="onFileChange"></v-file-input>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row v-if="loading">
      <v-row justify="center">
        <v-col cols="12" md="10">
          <v-skeleton-loader type="list-item-avatar, divider, list-item-three-line, card-heading, image, actions">
          </v-skeleton-loader>
        </v-col>
      </v-row>
    </v-row>

    <v-dialog v-model="dialogAttached" height="70vh" width="60%">
      <div style="
          position: relative;
          padding-bottom: 56.25%; /* 16:9 */
          padding-top: 25px;
          height: 0;
        ">
        <iframe style="position: absolute; top: 0; left: 0; width: 100%; height: 69vh" :src="selectedAttached"
          frameborder="0">
        </iframe>
      </div>
    </v-dialog>
    <v-dialog v-model="dialogCancelInvoice" transition="dialog-top-transition" max-width="600">
      <v-card>
        <v-toolbar color="primary" dark>Anular nota</v-toolbar>
        <v-card-text class="text-center">
          <div class="text-h5 pa-5 text-center">
            Seguro que deseas eliminar esta nota?
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn text @click="dialogCancelInvoice = false">Volver</v-btn>
          <v-btn outlined @click="cancelInvoice">Anular nota</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogAccountingInvoice" transition="dialog-top-transition" max-width="600">
      <v-card>
        <v-toolbar color="primary" dark>Contabilizar</v-toolbar>
        <v-card-text class="text-center">
          <div class="text-h5 pa-5 text-center">
            Seguro que deseas recontabilizar esta nota?
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn text @click="dialogAccountingInvoice = false">Volver</v-btn>
          <v-btn color="primary" outlined @click="accountingInvoice">Contabilizar nota</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogCancelAccountingInvoice" transition="dialog-top-transition" max-width="600">
      <v-card>
        <v-toolbar color="primary" dark>Contabilizar</v-toolbar>
        <v-card-text class="text-center">
          <div class="text-h5 pa-5 text-center">
            Seguro que deseas anular la contabilidad de esta factura?
          </div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn text @click="dialogCancelAccountingInvoice = false">Volver</v-btn>
          <v-btn color="primary" outlined @click="cancelAccountingInvoice">descontabilizar nota</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>
<style scoped>
.folded-paper:before {
  content: "";
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
  position: absolute;
  top: 0;
  right: 0;
  border-width: 0 30px 30px 0;
  border-style: solid;
  border-color: rgb(74, 92, 67) #fff;
  transition: 0.3s;
  border-radius: 0 0 0 4px;
}

.box {
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.034), 0 6.7px 5.3px rgba(0, 0, 0, 0.048),
    0 12.5px 10px rgba(0, 0, 0, 0.06), 0 22.3px 17.9px rgba(0, 0, 0, 0.072),
    0 41.8px 33.4px rgba(0, 0, 0, 0.086), 0 100px 80px rgba(0, 0, 0, 0.12);
}

.width-label {
  min-width: 110px;
  display: inline-block;
}

.border-right-light {
  border-right: 1px solid #f1f5f9;
}

.ncf-hero-bar {
  background: linear-gradient(to right, #ffffff, #f8fafc);
  border: 1px solid #e2e8f0 !important;
}

.ncf-accent-bg {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: var(--v-primary-base);
  opacity: 0.1;
}

.line-height-1 {
  line-height: 1 !important;
}

.uppercase {
  text-transform: uppercase;
}

.folded_corner {
  position: relative;
  width: 100%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: linear-gradient(to left bottom, transparent 50%, rgba(0, 0, 0, 0.05) 0) no-repeat 100% 0 / 2em 2em,
    linear-gradient(-135deg, transparent 1.5em, white 0);
}

.folded_corner::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 40px 40px 0;
  border-color: transparent transparent rgba(0, 0, 0, 0.05) transparent;
  pointer-events: none;
}

.custom-table {
  background-color: transparent !important;
}

.custom-table th {
  background-color: #f8f9fa !important;
  text-transform: uppercase;
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #5c6bc0 !important;
}

.glow-text {
  text-shadow: 0 0 15px rgba(var(--v-primary-base), 0.2);
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}

.border-left-soft {
  border-left: 1px solid #edf2f7;
}

.animate__animated {
  animation-duration: 0.6s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate__fadeIn {
  animation-name: fadeIn;
}

.header-meta-row {
  background: #f8fafc;
  padding: 8px 20px;
  border-radius: 12px;
  border: 1px solid #edf2f7;
  box-shadow: inset 0 1px 2px rgba(60, 64, 67, 0.05);
}

.signature-box {
  padding-top: 2rem;
  max-width: 300px;
}

.signature-box.elaborated {
  border-top: none;
}

.note-section {
  background-color: #f8fafc !important;
  border: 1px dashed #cbd5e1 !important;
  position: relative;
}

.note-section::before {
  content: " ";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: #cbd5e1;
  border-radius: 4px 0 0 4px;
}

.modern-tabs .v-tab--active {
  background: rgba(var(--v-primary-base), 0.05);
  border-bottom: 2px solid var(--v-primary-base);
}

.modern-inner-table.v-data-table {
  background: transparent !important;
}

.modern-inner-table.v-data-table thead th {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.05em;
  color: #7f8c8d !important;
  border-bottom: 1px solid #edf2f7 !important;
}

.modern-inner-table.v-data-table tbody td {
  font-size: 0.875rem !important;
  padding: 1rem !important;
  border-bottom: 1px solid #f8fafc !important;
}

.cursor-pointer {
  cursor: pointer;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.border-dashed {
  border: 2px dashed #edf2f7 !important;
}

@keyframes pulse-soft {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.02);
    opacity: 0.8;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.pulse-active {
  animation: pulse-soft 2s infinite ease-in-out;
}

/* Expert UI Style Tokens */
.summary-card {
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.08) !important;
}

.card-total {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, #ffffff 100%) !important;
}

.card-withheld {
  background: linear-gradient(135deg, rgba(211, 47, 47, 0.05) 0%, #ffffff 100%) !important;
}

.card-credits {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, #ffffff 100%) !important;
}

.card-paid {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, #ffffff 100%) !important;
}

.card-pending {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, #ffffff 100%) !important;
}

.gap-4 {
  gap: 16px;
}

.gap-2 {
  gap: 8px;
}

.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.italic {
  font-style: italic;
}

.totals-display {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

.border {
  border: 1px solid #e2e8f0 !important;
}

.overflow-hidden {
  overflow: hidden !important;
}

.position-relative {
  position: relative !important;
}

.bg-white {
  background-color: #ffffff !important;
}

.text-decoration-underline {
  text-decoration: underline !important;
}
</style>
<script>
import moment from "moment";
import PostAccounting from "@/components/Accounting/PostAccounting.vue";
export default {
  components: {
    PostAccounting
  },
  data: () => ({
    dialogCampos: false,
    hasPreviousPage: false,
    new_data_invoices: [],
    hasFiles: false,
    dialogCancelInvoice: false,
    dialogAccountingInvoice: false,
    dialogCancelAccountingInvoice: false,
    search: "",
    loading: false,
    selectedAttached: "",
    loadingAttachment: false,
    dialogAttached: false,
    date: moment().startOf("month").format("YYYY-MM-DD"),
    date2: moment().startOf("month").format("YYYY-MM-DD"),
    id_invoice: null,
    storage_invoice: "",
    totals_invoices: {},
    data_invoice: null,
    detailsItems: [],
    tab: null,
    accounting: [],
    invoice_details: [],
    paymentsInvoice: [],
    invoice_groups: [],
    invoice_warehouse_details: [],
    locationImage: null,
    storage_json: [],
    is_valid_dgii: false,
    headersContabilidad: [
      { text: "Asiento", align: "start", sortable: false, value: "diary_book_code" },
      { text: "Cuenta", value: "chart_of_account_name" },
      { text: "Location", value: "location_description" },
      { text: "Ent.", value: "entity_name" },
      { text: "Cat.", value: "category_name" },
      { text: "Sub Cat.", value: "subcategory_name" },
      { text: "Déb trans", value: "debito", align: "right" },
      { text: "Cré trans", value: "credito", align: "right" },
      { text: "Débito", value: "debitoBase", align: "right" },
      { text: "Crédito", value: "creditoBase", align: "right" },
    ],
    headersInvoice: [
      {
        text: "Factura",
        align: "start",
        sortable: false,
        value: "invoice_number",
      },
      { text: "Cliente", value: "entity_name" },
      { text: "Fecha", value: "emission_date" },
      { text: "	Monto", value: "total_amount", align: "right" },
    ],
    headersInventario: [
      { text: "	Item", value: "item_name" },

      { text: "Cantidad", value: "quantity" },
      { text: "	Almacén", value: "warehouse_name" },

      { text: "	Tipo", value: "entry_type_name" },
      {
        text: "Fecha",
        align: "start",
        sortable: false,
        value: "effective_date",
      },
    ],

    headers: [
      {
        text: "ID",
        align: "start",
        sortable: false,
        value: "item_id",
      },
      {
        text: "Producto/servicio",
        align: "start",
        sortable: false,
        value: "item_name",
      },
      { text: "Referencia", value: "item_description" },
      { text: "Precio", value: "price", align: "end", width: "15%" },
      { text: "	Cantidad", value: "quantity", align: "end" },

      { text: "	Impuesto", value: "itbis", align: "end" },

      { text: "	Total", value: "total", align: "end" },
    ],
    itemsFiles: [],
    headersFiles: [
      {
        text: "Descripcion",
        align: "start",
        sortable: false,
        value: "description",
      },

      { text: "	Action", value: "actions", align: "end" },
    ],
    commentHeader: "",
  }),
  computed: {
    post_accounting_computed() {
      return this.accounting.map(entry => {
        return {
          order_chart_of_accounts: [{
            chart_of_account_id: entry.chart_of_account_id,
            chart_of_account_code: entry.chart_of_account_code,
            chart_of_account_name: entry.chart_of_account_name,
            category_id: entry.category_id,
            subcategory_id: entry.subcategory_id,
            location_id: entry.location_id,
            department_id: entry.department_id,
            entry_type_id: entry.entry_type_id,
            amount: parseFloat(entry.amount),
            currency_rate: parseFloat(entry.currency_rate),
            currency_id: entry.currency_id,
            diary_book_id: entry.diary_book_id,
            entity_id: entry.entity_id,
            entity_name: entry.entity_name,
          }],
          entity_name: entry.entity_name,
          item_name: "Entrada Contable", // Valor por defecto ya que no existe en la estructura original
          currency_code: entry.currency_code
        };
      });
    },
    can_edit_accounting() {
      if ([1, 4].includes(this.data_invoice.invoice_type_id) && this.$store.state.auth.globalFeatures.includes('SHOP-022')) {
        return true;
      }
      if ([2, 3].includes(this.data_invoice.invoice_type_id) && this.$store.state.auth.globalFeatures.includes('ING-042')) {
        return true;
      }
      return false;
    },
    itemsTab() {
      let data = [];

      data.push("Contabilidad");
      data.push("Inventario");

      return data;
    },

    formTitle() {
      if (this.data_invoice.invoice_type_id === 2 && this.data_invoice.note_type == "CREDIT") {
        return "NOTA DE CREDITO DE VENTA";
      }
      if (this.data_invoice.invoice_type_id === 3 && this.data_invoice.note_type == "CREDIT") {
        return "NOTA DE CREDITO DOCUMENTO INTERNO POR COBRAR";
      }
      if (this.data_invoice.invoice_type_id === 1 && this.data_invoice.note_type == "CREDIT") {
        return "NOTA DE CREDITO DE COMPRA";
      }
      if (this.data_invoice.invoice_type_id === 4 && this.data_invoice.note_type == "CREDIT") {
        return "NOTA DE CREDITO DOCUMENTO INTERNO POR PAGAR";
      }
      if (this.data_invoice.invoice_type_id === 2 && this.data_invoice.note_type == "DEBIT") {
        return "NOTA DE DEBITO DE VENTA";
      }
      if (this.data_invoice.invoice_type_id === 3 && this.data_invoice.note_type == "DEBIT") {
        return "NOTA DE DEBITO DOCUMENTO INTERNO POR COBRAR";
      }
      if (this.data_invoice.invoice_type_id === 1 && this.data_invoice.note_type == "DEBIT") {
        return "NOTA DE DEBITO DE COMPRA";
      }
      if (this.data_invoice.invoice_type_id === 4 && this.data_invoice.note_type == "DEBIT") {
        return "NOTA DE DEBITO DOCUMENTO INTERNO POR PAGAR";
      }
    },
    totalContabilidad() {
      let debito = 0;
      let credito = 0;

      if (this.accounting.length > 0) {
        for (let item of this.accounting) {
          if (item.entry_type_id == 1) {
            debito += parseFloat(item.amount);
          }
          if (item.entry_type_id == 2) {
            credito += parseFloat(item.amount);
          }
        }
      }

      return {
        debito: parseFloat(debito).toFixed(2),
        credito: parseFloat(credito).toFixed(2),
      };
    },
    summaryCards() {
      if (!this.data_invoice) return [];
      const isPayable = [1, 4].includes(this.data_invoice?.invoice_type_id);
      return [
        {
          title: "Valor Ajuste",
          value: this.data_invoice.total_amount,
          icon: "mdi-currency-usd",
          iconColor: "primary",
          colorClass: "card-total"
        },
        {
          title: "Subtotal",
          value: this.data_invoice.subtotal_amount,
          icon: "mdi-sigma",
          iconColor: "grey",
          colorClass: "card-credits"
        },
        {
          title: "Impuestos",
          value: this.data_invoice.taxes_amount,
          icon: "mdi-percent",
          iconColor: "error",
          colorClass: "card-withheld"
        }
      ];
    },
    isExpired() {
      // For notes this might not be relevant but kept for design consistency
      return false;
    }
  },
  created() {
    this.getData();
  },
  methods: {
    save_acounting_invoice(accounting) {
      this.pre_accounting_saved = accounting;
      this.unsaved_changes = false;
    },
    async loadLogo() {
      if (this.data_invoice.image_storage != null) {
        this.locationImage = await this.get_storage(this.data_invoice.image_storage);
      } else {
        this.locationImage = null;
      }
    },
    getDate(date, type) {
      let newDate = "";
      if (type == 1) {
        newDate = moment(date).format("DD/MM/YYYY hh:mm:ss A").toString();
      }
      if (type == 2) {
        newDate = moment(date).format("DD-MM-YYYY").toString();
      }
      if (type == 3) {
        newDate = moment(date).format("DD-MMMM-YYYY").toString();
      }
      return newDate;
    },
    currencyFormat(value) {
      if (value === null || value === undefined) return "0.00";
      return parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getInitials(name) {
      if (!name) return "";
      const parts = name.split(" ");
      let initials = "";
      for (let i = 0; i < Math.min(parts.length, 2); i++) {
        if (parts[i].length > 0 && parts[i] !== "") {
          initials += parts[i][0];
        }
      }
      return initials.toUpperCase();
    },
    getDocumentTypeName(documentNumber) {
      if (!documentNumber) return "Documento no Identificado";
      // Extract the two digits after the first character
      const documentTypeCode = documentNumber.slice(1, 3);

      // Comprehensive mapping of DGII document type codes to names
      const types = {
        // Facturas
        "31": "Factura de Crédito Fiscal Electrónica",
        "32": "Factura de Consumo Electrónica",
        "33": "Nota de Débito Electrónica",
        "34": "Nota de Crédito Electrónica",
        "36": "Cotización",

        // Otros documentos fiscales
        "01": "Factura de Crédito Fiscal",
        "02": "Factura de Consumo",
        "03": "Nota de Débito",
        "04": "Nota de Crédito",
        "11": "Gastos Menores",
        "12": "Registro Único de Proveedores (RUP)",
        "13": "Comprobante Fiscal Simple",
        "14": "Comprobante Fiscal Gubernamental",
        "15": "Comprobante Fiscal Especial",

        // Documentos de exportación
        "41": "Comprobante Electrónico de Compras",
        "43": "Comprobante Electrónico para Gastos Menores",
        "44": "Comprobante Electrónico para Regímenes Especiales",
        "45": "Comprobante Electrónico Gubernamental",
        "46": "Comprobante Electrónico para Exportaciones",
        "47": "Comprobante Electrónico para Pagos al Exterior",
      };

      // Return the document type name or a default
      return types[documentTypeCode] || "Documento no Identificado";
    },
    viewEntity(id) {
      this.$router.push({
        name: "EntitiesDetails",
        params: { id: id },
      });
    },
    showInvoice(id, isFromNote) {
      this.$router.push({
        name: "ViewInvoice",
        params: { id: id },
      });
    },
    beforeRoute() {
      this.$router.go(-1);
    },
    async saveCampos() {
      let data = {
        id: this.data_invoice.invoice_id,
        supplier_invoice_number:
          this.data_invoice.supplier_invoice_number == null
            ? ""
            : this.data_invoice.supplier_invoice_number,
        supplier_invoice_voucher:
          this.data_invoice.supplier_invoice_voucher == null
            ? ""
            : this.data_invoice.supplier_invoice_voucher,
      };
      const responseInvoice = await this.api_Post(
        "income/update_invoice_supplier_data",
        data
      );

      if (responseInvoice.status == 200) {
        this.getData();
        this.dialogCampos = false;
        this.$toast.success("Actualizado");
      } else {
        this.$toast.error("Error al actualizar");
      }
    },
    async getData() {
      this.loading = true;
      this.id_invoice = this.$route.params.id;

      if (window.history.length > 1) {
        this.hasPreviousPage = true;
      } else {
        this.hasPreviousPage = false;
      }
      let filters = "where invoice_notes.id=" + this.id_invoice;

      let responseInvoice = await this.api_Post("income/note_list", {
        filters: filters,
      });
      if (responseInvoice.status == 200) {
        let data = await responseInvoice.json();

        this.data_invoice = data[0];

        // Check if note exists in DGII
        if (data[0].use_ecf) {
          const responseNoteExistsDgii = await this.api_Post(
            "income/search_invoice_exists_dgii",
            {
              ncf_invoice_number: data[0].note_ncf,
              business_unit_rnc: data[0].business_unit_rnc
            }
          );
          let exits_in_dgii = await responseNoteExistsDgii.json();

          if (exits_in_dgii.length > 0) {
            if (exits_in_dgii[0].Estado == "ACEPTADO") {
              this.is_valid_dgii = true;
            }
          }
        }

        const responseAccountig = await this.api_Post("accounting/payment_accounting", {
          filters: `  where invoice_note_id =${this.id_invoice}`,
        });
        if (responseAccountig.status == 200) {
          this.accounting = await responseAccountig.json();
        }

        const responseNoteWarehouse = await this.api_Post(
          "income/search_invoice_note_warehouse",
          {
            number: this.id_invoice,
          }
        );
        if (responseNoteWarehouse.status == 200) {
          this.invoice_warehouse_details = await responseNoteWarehouse.json();
        }
        this.loading = false;

        const exists = this.$store.state.auth.all_locations.some(
          (location) => location.id === this.data_invoice.location_id
        );

        // if (exists == false) {
        //   this.$router.push({ name: "Home" });
        // }

        //  if (this.data_invoice.recorded == true) {

        this.loadLogo();
        this.itemsFiles = this.data_invoice.storage_json;

        if (this.data_invoice.storage_json == null) {
        } else {
          this.storage_json = this.data_invoice.storage_json;
        }
      } else {
        this.$toast.error("error al cargar la factura!");
      }

      this.loading = false;
    },
    viewReception(id, order) {
      // this.$router.push({
      //     name: "ViewReceptions",
      //     params: { order_id: this.data_order[0].order_id, id: id },
      // });
    },
    async sendToDGII() {
      const resultado = await this.get_dgii_json(this.data_invoice.invoice_note_number);

      const cabecera = {
        "Content-Type": "application/json",
        "X-API-KEY": "dev-dgii-ecf-api-key-2023",
      };

      const url = process.env.VUE_APP_API_DGII;
      const ambiente_dgii =
        process.env.NODE_ENV === "production"
          ? process.env.VUE_APP_API_DGII_AMBIENTE
          : process.env.VUE_APP_API_DGII_AMBIENTE_TEST;

      const request = {
        method: "POST",
        headers: cabecera,
        body: JSON.stringify(resultado),
      };


      // const response = await fetch(
      //   url +
      //     `api/${ambiente_dgii}/${resultado.ECF.Encabezado.Emisor.RNCEmisor}/Invoice/create`,
      //   request
      // );
    },
    async cancelInvoice() {
      let result = await this.api_Post("income/cancel_invoice_note", {
        invoice_note_id: this.data_invoice.id,
        user_id: this.$store.state.auth.user.user.user_id,
      });
      if (result.status == 200) {
        this.$toast.success("Factura anulada");
        this.dialogCancelInvoice = false;
        this.getData();
      } else {
        this.$toast.error("Error al anular la factura");
      }
    },
    // Extrae el mensaje real que devuelve el backend (ej. el RAISE EXCEPTION
    // de la función de Postgres) para mostrarlo en vez de un mensaje genérico.
    async extractBackendError(response) {
      try {
        const text = await response.text();
        if (!text) return null;
        try {
          const data = JSON.parse(text);
          return data.message || data.detail || data.error || data.hint || text;
        } catch (e) {
          return text;
        }
      } catch (e) {
        return null;
      }
    },
    async accountingInvoice() {
      let result = await this.api_Post("income/accounting_note", {
        invoice_id: this.id_invoice,
      });
      if (result.status == 200) {
        this.getData();
        this.$toast.success("Factura contabilizada");
        this.dialogAccountingInvoice = false;
      } else {
        const msg = await this.extractBackendError(result);
        console.error("Error al contabilizar la nota:", msg);
        this.$toast.error(msg || "Error al contabilizar la factura", { timeout: 10000 });
      }
    },
    async cancelAccountingInvoice() {
      let result = await this.api_Post("income/accounting_note_cancel", {
        invoice_id: this.id_invoice,
      });
      if (result.status == 200) {
        this.$toast.success("Factura descontabilizada");
        this.dialogCancelAccountingInvoice = false;
        this.getData();
      } else {
        const msg = await this.extractBackendError(result);
        console.error("Error al descontabilizar la nota:", msg);
        this.$toast.error(msg || "Error al descontabilizar la factura", { timeout: 10000 });
      }
    },

    getTotalDebito() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 1 ? acc + parseFloat(entry.amount) : acc;
      }, 0);
    },
    getTotalCredito() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 2 ? acc + parseFloat(entry.amount) : acc;
      }, 0);
    },
    getTotalDebitoBase() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 1
          ? acc + parseFloat(entry.amount * entry.currency_rate)
          : acc;
      }, 0);
    },
    getTotalCreditoBase() {
      return this.accounting.reduce((acc, entry) => {
        return entry.entry_type_id === 2
          ? acc + parseFloat(entry.amount * entry.currency_rate)
          : acc;
      }, 0);
    },
    async viewAddtached(item) {
      this.loadingAttachment = true;
      this.dialogAttached = true;

      try {
        this.selectedAttached = await this.get_storage(item.record);
      } catch (err) { }

      this.loadingAttachment = false;
    },
    async onFileChange() {
      try {
        const fileInput = document.getElementById("fileInput");
        let record = await this.upload_images("attached_invoices", fileInput);

        this.storage_json.push({
          description: "File " + (this.storage_json.length + 1).toString(),
          record: record,
        });

        const responseRequest = await this.api_Post("income/attaches_invoices", {
          id: this.data_invoice.invoice_id,
          json: this.storage_json,
        });
        if (responseRequest.status == 200) {
          this.$toast.success("Guardado!");
          this.getData();
        } else {
          this.$toast.error("error al cargar!");
        }
      } catch (e) {
        this.$toast.error("error " + e.message);
      }
    },

    takeImage() {
      //this.fileInput.click();
      document.getElementById("fileInput").click();

      //this.this.dialogImage = true;
    },
    downloadInvoice() {
      this.$router.push({
        name: "DownloadNote",
        params: { id: this.data_invoice.id },
      });
    },
    selectItem(item) {
      this.selectedEntity = item;
      this.dialogSearch = false;
    },
    selectPayment(item) {
      this.selectedPayments = item;
      this.dialogPayments = false;
    },
    deleteItem(item) {
      let editIndex = this.selectedPayments.indexOf(item);

      this.selectedPayments.splice(editIndex, 1);
    },
  },
};
</script>
